/**
 * F10 Projecten — gedeelde DTO's, Zod-schemas en label-maps.
 *
 * Concept: project-overzicht voor PLANNER/ADMIN/TEAMLEIDER met budget,
 * voortgang, team-rolverdeling en marge-rapportage. Een project is een
 * umbrella over meerdere Werkbonnen / Orders die samen een grotere
 * opdracht vormen. Werkbonnen kunnen optioneel naar een project
 * verwijzen voor uren-aggregatie en marge-berekening.
 *
 * Backoffice-only beheermodule (geen portal-flow). Permission-key PROJECTEN.
 *
 * Bron-hierarchie:
 *   - fs-pro `src/mock/projects.ts` (datamodel + helpers calcMarge / isOverBudget)
 *   - fs-pro `src/pages/projecten/ProjectenPage.tsx` (UI: lijst + 4-tabs detail-modal)
 *   - productblauwdruk-monteursapp.md §7.2.15 (project-tabel)
 *   - HANDOVER §5.17 (Projecten) — minimaal beschreven
 */
import { z } from 'zod'

// ─── Enums ──────────────────────────────────────────────────────────────────

export const PROJECT_STATUSSEN = [
  'CONCEPT',
  'ACTIEF',
  'ON_HOLD',
  'AFGEROND',
  'GEANNULEERD',
] as const
export type ProjectStatus = (typeof PROJECT_STATUSSEN)[number]

export const PROJECT_MEMBER_ROLLEN = ['LEAD', 'MONTEUR', 'SPECIALIST'] as const
export type ProjectMemberRol = (typeof PROJECT_MEMBER_ROLLEN)[number]

// ─── Labels ─────────────────────────────────────────────────────────────────

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  CONCEPT: 'Concept',
  ACTIEF: 'Actief',
  ON_HOLD: 'In wacht',
  AFGEROND: 'Afgerond',
  GEANNULEERD: 'Geannuleerd',
}

export const PROJECT_MEMBER_ROL_LABELS: Record<ProjectMemberRol, string> = {
  LEAD: 'Lead',
  MONTEUR: 'Monteur',
  SPECIALIST: 'Specialist',
}

export const PROJECT_STATUS_BADGE: Record<
  ProjectStatus,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'info'
> = {
  CONCEPT: 'secondary',
  ACTIEF: 'success',
  ON_HOLD: 'warning',
  AFGEROND: 'info',
  GEANNULEERD: 'destructive',
}

// ─── Zod-schemas ────────────────────────────────────────────────────────────

const optionalString = z
  .string()
  .trim()
  .max(2000)
  .optional()
  .nullable()
  .transform((v) => (v == null || v.trim() === '' ? null : v))

const memberInputSchema = z.object({
  employeeId: z.string().min(1),
  rol: z.enum(PROJECT_MEMBER_ROLLEN).default('MONTEUR'),
  geplandUren: z.number().min(0).max(100_000).default(0),
  gerealiseerdeUren: z.number().min(0).max(100_000).default(0),
})

export const createProjectSchema = z
  .object({
    naam: z.string().trim().min(1, 'Naam is verplicht').max(200),
    clientId: z.string().min(1, 'Klant is verplicht'),
    locatie: optionalString,
    status: z.enum(PROJECT_STATUSSEN).default('CONCEPT'),
    startdatum: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum in YYYY-MM-DD formaat')
      .optional()
      .nullable(),
    einddatum: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum in YYYY-MM-DD formaat')
      .optional()
      .nullable(),
    voortgang: z.number().int().min(0).max(100).default(0),
    omschrijving: optionalString,

    // Budget-velden
    geplandUren: z.number().min(0).max(1_000_000).default(0),
    gerealiseerdeUren: z.number().min(0).max(1_000_000).default(0),
    kostprijsPerUur: z.number().min(0).max(10_000).default(0),
    verkoopprijsPerUur: z.number().min(0).max(10_000).default(0),
    materiaalkosten: z.number().min(0).max(100_000_000).default(0),
    materiaalBudget: z.number().min(0).max(100_000_000).default(0),
    reiskosten: z.number().min(0).max(100_000_000).default(0),
    reiskostenBudget: z.number().min(0).max(100_000_000).default(0),

    members: z.array(memberInputSchema).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.startdatum && val.einddatum && val.einddatum < val.startdatum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['einddatum'],
        message: 'Einddatum moet op of na startdatum liggen',
      })
    }
  })
export type CreateProjectInput = z.infer<typeof createProjectSchema>

// PATCH = partial. superRefine is bewust niet meegekopieerd zodat losse veld-
// updates (bv. alleen voortgang) zonder error door kunnen.
export const updateProjectSchema = z.object({
  naam: z.string().trim().min(1).max(200).optional(),
  clientId: z.string().min(1).optional(),
  locatie: optionalString,
  status: z.enum(PROJECT_STATUSSEN).optional(),
  startdatum: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .nullable(),
  einddatum: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .nullable(),
  voortgang: z.number().int().min(0).max(100).optional(),
  omschrijving: optionalString,

  geplandUren: z.number().min(0).max(1_000_000).optional(),
  gerealiseerdeUren: z.number().min(0).max(1_000_000).optional(),
  kostprijsPerUur: z.number().min(0).max(10_000).optional(),
  verkoopprijsPerUur: z.number().min(0).max(10_000).optional(),
  materiaalkosten: z.number().min(0).max(100_000_000).optional(),
  materiaalBudget: z.number().min(0).max(100_000_000).optional(),
  reiskosten: z.number().min(0).max(100_000_000).optional(),
  reiskostenBudget: z.number().min(0).max(100_000_000).optional(),
  // batch-65 inkoop-kosten (FO-483 §3.9, A3) — materiaalkosten handmatig
  // overschrijven (true) of afleiden uit de toegerekende inkoop (false). Alleen
  // met `true` telt `materiaalkosten` hierboven; elke wissel en elk handmatig
  // bedrag wordt geauditeerd.
  materiaalkostenHandmatig: z.boolean().optional(),
  // Geen `members` op update — daarvoor zijn aparte member-routes.
  // Reden: PATCH op nested arrays geeft slordige semantiek (vervangen vs.
  // mergen). UI gebruikt voor leden POST/PATCH/DELETE op /projecten/:id/members.
})
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>

export const upsertProjectMemberSchema = z.object({
  rol: z.enum(PROJECT_MEMBER_ROLLEN).default('MONTEUR'),
  geplandUren: z.number().min(0).max(100_000).default(0),
  gerealiseerdeUren: z.number().min(0).max(100_000).default(0),
})
export type UpsertProjectMemberInput = z.infer<typeof upsertProjectMemberSchema>

export const listProjectQuerySchema = z.object({
  status: z.enum(PROJECT_STATUSSEN).optional(),
  clientId: z.string().min(1).optional(),
  zoek: z.string().trim().min(1).optional(),
  overBudget: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional(),
  page: z.coerce.number().int().min(1).default(1),
  // Cap 2000 zodat het projectoverzicht de volledige (gefilterde) set in één
  // keer kan ophalen en client-side globaal kan sorteren/pagineren.
  pageSize: z.coerce.number().int().min(1).max(2000).default(50),
})
export type ListProjectQuery = z.infer<typeof listProjectQuerySchema>

// ─── DTO's ──────────────────────────────────────────────────────────────────

export interface ProjectClientDTO {
  id: string
  name: string
}

export interface ProjectMemberDTO {
  id: string
  employeeId: string
  naam: string
  rol: ProjectMemberRol
  geplandUren: number
  gerealiseerdeUren: number
}

export interface ProjectWerkbonDTO {
  werkbonId: string
  werkbonNumber: string
  startDatum: string
  uren: number
  status: string
  /** Gefactureerd? Indicatie voor de werkbonnen-tab (icoon/badge). */
  isGefactureerd: boolean
  /** Som van invoice-line-totalen die deze werkbon factureerde (0 als concept). */
  gefactureerdBedrag: number
}

/**
 * Een planning-blok dat aan een project gekoppeld is (via `Planning.projectId`).
 * Voor de "Rooster"-tab op de project-detail.
 */
export interface ProjectPlanningDTO {
  id: string
  employeeId: string
  medewerker: string
  /** Optioneel locatie-label (`Location.name`). */
  locatie: string | null
  title: string
  startAt: string // ISO datetime
  endAt: string // ISO datetime
  status: string // PlanningStatus
}

export interface ProjectListRowDTO {
  id: string
  projectNummer: string
  naam: string
  status: ProjectStatus
  voortgang: number
  startdatum: string | null
  einddatum: string | null
  client: ProjectClientDTO
  locatie: string | null
  geplandUren: number
  gerealiseerdeUren: number
  kostprijsPerUur: number
  verkoopprijsPerUur: number
  materiaalkosten: number
  materiaalBudget: number
  reiskosten: number
  reiskostenBudget: number
  budgetOverschrijding: boolean
  // batch-65 inkoop-kosten (FO-483 §3.9, A3) — `materiaalkosten` hierboven is de
  // EFFECTIEVE waarde: afgeleid uit de toegerekende inkoopregels, of het
  // handmatig overschreven bedrag. `materiaalkostenAfgeleid` is altijd de
  // afgeleide som (ook als handmatig aan staat), voor de vergelijking.
  materiaalkostenHandmatig: boolean
  materiaalkostenAfgeleid: number

  // Berekend (uit @streep/utils — pure functies):
  marge: number
  margePercentage: number
  omzet: number
  kosten: number
  budgetBenuttingPercentage: number

  createdAt: string
  updatedAt: string
}

export interface ProjectDetailDTO extends ProjectListRowDTO {
  omschrijving: string | null
  members: ProjectMemberDTO[]
  /**
   * Werkbonnen-tab: alle (niet-soft-deleted) werkbonnen waarvoor
   * `projectId = this.id`. Gevuld via `Werkbon.projectId` FK (fase 2).
   */
  werkbonnen: ProjectWerkbonDTO[]
  /**
   * Rooster-tab: alle Planning-blokken waarvoor `projectId = this.id`.
   * Toont wie wanneer op dit project staat ingepland. (fase 2 FK.)
   */
  planning: ProjectPlanningDTO[]

  /**
   * Live-rendement (fase 5.1) — som van werkelijk gefactureerde invoice-line-
   * totalen voor deze werkbonnen, vs. de geboekte kosten. Werkelijke omzet i.t.t.
   * de theoretische `omzet`-waarde uit het project-record.
   */
  omzetGefactureerd: number
  rendement: number
  rendementPercentage: number
}

export interface ProjectStatsDTO {
  actiefAantal: number
  totaalOmzet: number
  gemiddeldeMargePercentage: number
  budgetOverschrijdingenAantal: number
}
